/// <reference path='common.ts' />
/// <reference path='element_bind.ts' />

/**
 * フォームに対する操作を行うクラスです。
 * data-bind属性に値を入れるとフォーム内の入力欄に反映され、入力欄を変更するとdata-bind属性の値が変更されます。
 * 名前にドットを入れると入れ子として扱われます（data-form-objectと同等）。
 * 名前やグループの後ろに"[]"を付けると配列として扱われます。
 *
 * 属性
 * data-form-arg: data-bind属性に設定する値の変数名（省略時は展開する）
 * data-form-fixed: フォーム内の入力欄が変更されてもdata-bind属性の値を変更しない場合
 * data-form-change-click: フォーム内の入力欄が変更された場合にクリックするエレメントのセレクタ（クリック先でフォームを変更すると無限ループになります）
 *
 * 属性（フォーム中のボタン）
 * data-form-disabled: 通信中でdisabledになっている場合（変更しないでください）
 *
 * 属性（フォーム中のファイル選択）
 * data-form-file-data: base64データを設定するセレクタかパラメータ名（inputタグのname属性の値、省略時は当該エレメントでdata-form-data属性に設定する）
 * data-form-file-text: テキストデータを設定するセレクタかパラメータ名（inputタグのname属性の値、省略時は当該エレメントでdata-form-data属性に設定する）
 * data-form-file-type: データのContentTypeを設定するパラメータ名（inputタグのname属性の値）
 * data-form-file-name: データのファイル名を設定するパラメータ名（inputタグのname属性の値）
 * data-form-file-encoding: データファイル解析時の文字コード
 * data-form-data: data-form-file-data属性の値が空の場合に設定されるファイルのデータ
 *
 * 属性（フォーム中の任意のエレメント）
 * data-form-join: エレメントがテキストエリアの場合、値が配列の場合に改行文字で結合します
 * data-form-object: 子オブジェクト名（配列の場合は名称の後ろに[]を付けます）
 * data-form-reset-bind: リセット時に対象エレメントのdata-bind属性も削除されます
 * data-form-list: 次に記載するリスト用属性の基点
 * data-form-list-add: 所属しているdata-form-objectエレメントの子エレメントをクローンして下に追加します（中身はリセットします）
 * data-form-list-delete: 所属しているdata-form-objectエレメントの子エレメントを削除します（最後の一つの場合は削除しません）
 * data-form-list-prev: 所属しているdata-form-objectエレメントの子エレメントを前のエレメントと入れ替えます（最初の場合は入れ替えません）
 * data-form-list-next: 所属しているdata-form-objectエレメントの子エレメントを次のエレメントと入れ替えます（最後の場合は入れ替えません）
 * data-form-default: hiddenエレメントのvalue属性の値（変更しないで下さい）
 *
 * イベント
 * add-message: formもしくは入力エレメントにメッセージが追加された場合（detailはメッセージ）
 * clear-messages: メッセージのクリア要求あった場合
 * fetch: 通信が完了した場合（detailはrequestとresponse）
 * load: name="file"エレメントのファイルの読み込みが完了した場合
 */
class HaoriForm {
  /** オプション */
  static options: HaoriFormOptions;

  /**
   * @param options オプション設定
   */
  static setOptions(options: HaoriFormOptions) {
    HaoriForm.options = (<any>Object).assign(new HaoriFormOptions(), options);
  }

  /**
   * フォームにあるボタンを無効化します。
   *
   * @param form フォームエレメント
   */
  static disableButtons(form: HTMLFormElement) {
    form
      .querySelectorAll(
        'input[type="submit"]:not([disabled]),input[type="reset"]:not([disabled]),button:not([disabled])'
      )
      .forEach((element) => {
        element.setAttribute('data-form-disabled', '');
        element.setAttribute('disabled', '');
      });
  }

  /**
   * フォームにある無効化されたボタンを再有効化します。disableButtonsで無効化されたもののみを対象とします。
   *
   * @param form フォームエレメント
   */
  static enableButtons(form: HTMLFormElement) {
    form
      .querySelectorAll(
        'input[type="submit"][data-form-disabled],input[type="reset"][data-form-disabled],button[data-form-disabled]'
      )
      .forEach((element) => {
        element.removeAttribute('data-form-disabled');
        element.removeAttribute('disabled');
      });
  }

  /**
   * フォーム内の入力エレメントに設定されている値をオブジェクトとして取得します。
   *
   * @param form フォームエレメント
   */
  static getValues(form: HTMLFormElement): HashObject {
    return this.getChildValues(form, []);
  }

  /**
   * 親エレメント内の入力エレメントに設定されている値をオブジェクトとして取得します。
   *
   * @param parent 親エレメント
   * @param elements 処理済みエレメントのリスト
   */
  private static getChildValues(parent: Element, elements: Element[]): HashObject {
    let result = new HashObject();
    parent.querySelectorAll('[data-form-object]').forEach((element: Element) => {
      let isDone = false;
      elements.forEach((done) => {
        if (isDone == false && done == element) {
          isDone = true;
        }
      });
      if (isDone) {
        return;
      }
      elements.push(element);
      let name = <string>element.getAttribute('data-form-object');
      let subName: string | null = null;
      if (name.match(/\[\]$/)) {
        subName = name.substring(0, name.length - 2);
        if (Array.isArray(result[subName]) == false) {
          result[subName] = [];
        }
      }
      let child = HaoriForm.getChildValues(element, elements);
      if (child == null) {
        return;
      }
      if (Array.isArray(child)) {
        if (child.length == 0) {
          return;
        }
      } else if (typeof child == 'object') {
        if (Object.keys(child).length == 0) {
          return;
        }
      }
      if (subName) {
        result[subName].push(child);
      } else {
        result[name] = child;
      }
    });
    parent.querySelectorAll('[name]').forEach((element: Element) => {
      let target = result;
      let isDone = false;
      elements.forEach((done) => {
        if (isDone == false && done == element) {
          isDone = true;
        }
      });
      if (isDone) {
        return;
      }
      let name = <string>element.getAttribute('name');
      if (name.indexOf('.') >= 0) {
        let parts = name.split('.');
        for (let i = 0; i < parts.length - 1; i++) {
          let part = parts[i];
          if (part.endsWith('[]')) {
            part = part.substring(0, part.length - 2);
            if (!Array.isArray(target[part])) {
              target[part] = [];
            }
            let object = {};
            target[part].push(object);
            target = object;
          } else {
            if (typeof target[part] != 'object') {
              target[part] = {};
            }
            target = target[part];
          }
        }
        name = parts[parts.length - 1];
      }
      let subName: string | null = null;
      if (name.endsWith('[]')) {
        subName = name.substring(0, name.length - 2);
        if (!Array.isArray(target[subName])) {
          target[subName] = [];
        }
      }
      let value: any;
      switch (element.tagName.toLowerCase()) {
        case 'input': {
          let input = <HTMLInputElement>element;
          let type = element.getAttribute('type');
          value = input.value;
          if (type != 'checkbox' && value == '') {
            return;
          }
          switch (type) {
            case 'checkbox':
            case 'radio':
              if (input.checked) {
                if (input.getAttribute('value') == 'true') {
                  value = true;
                } else if (input.getAttribute('value') == 'false') {
                  value = false;
                }
              } else {
                value = null;
              }
              break;
            case 'number':
              value = parseFloat(value);
              break;
            case 'file':
              value = input.getAttribute('data-form-data');
              break;
          }
          break;
        }
        case 'textarea': {
          value = (<HTMLTextAreaElement>element).value;
          if (value == '') {
            return;
          }
          if (element.hasAttribute('data-form-join')) {
            value = (<string>value).split(/\r?\n/g);
          }
          break;
        }
        case 'select': {
          value = (<HTMLSelectElement>element).value;
          if (value == '') {
            return;
          }
          break;
        }
      }
      if (value === null) {
        return;
      }
      if (subName) {
        (<any[]>target[subName]).push(value);
      } else {
        target[name] = value;
      }
      elements.push(element);
    });
    return result;
  }

  /**
   * 設定値をクエリパラメータとして取得します。
   */
  static getQueryParameter(form: HTMLFormElement): string {
    return this.getQueryParameterFromObject(this.getValues(form));
  }

  /**
   * オブジェクトをクエリパラメータとして取得します。
   *
   * @param object オブジェクト
   * @param prefix パラメータ名のプレフィックス
   */
  static getQueryParameterFromObject(object: HashObject, prefix: string | null = null): string {
    let params: string[] = [];
    for (let key in object) {
      let value = object[key];
      if (Array.isArray(value)) {
        value.forEach((child) => {
          if (typeof child == 'object') {
            params.push(HaoriForm.getQueryParameterFromObject(child, prefix == null ? key : prefix + '.' + key));
          } else {
            params.push(
              encodeURIComponent(prefix == null ? key : prefix + '.' + key) + '=' + encodeURIComponent(child.toString())
            );
          }
        });
      } else if (typeof value == 'object') {
        params.push(HaoriForm.getQueryParameterFromObject(value, prefix == null ? key : prefix + '.' + key));
      } else {
        params.push(encodeURIComponent(prefix == null ? key : prefix + '.' + key) + '=' + encodeURIComponent(value));
      }
    }
    return params.join('&');
  }

  /**
   * 設定値を FormData オブジェクトとして取得します。
   * multipart/form-data で送信する場合に利用します。
   *
   * @param form フォームエレメント
   */
  static getFormData(form: HTMLFormElement): FormData {
    let data = new FormData();
    form.querySelectorAll('[name]').forEach((element) => {
      let name = element.getAttribute('name')!;
      switch (element.tagName) {
        case 'INPUT':
          if (element.getAttribute('type') == 'file') {
            let files = (<HTMLInputElement>element).files;
            if (files == null) {
              return;
            }
            for (let i = 0; i < files.length; i++) {
              data.append(name, files[i]);
            }
          } else {
            data.append(name, (<HTMLInputElement>element).value);
          }
          break;
        case 'SELECT':
        case 'TEXTAREA':
          data.append(name, (<HTMLSelectElement | HTMLTextAreaElement>element).value);
          break;
        default:
        // 想定外のタグはスキップする
      }
    });
    return data;
  }

  /**
   * 親エレメント内の入力エレメントに値を設定します。
   *
   * @param parent 親エレメント
   * @param values 設定する値
   * @param bind data-bindにも反映させる場合はtrue
   */
  static setValues(parent: Element, values: HashObject, bind = true) {
    if (parent instanceof HTMLFormElement) {
      HaoriForm.reset(<HTMLFormElement>parent, false);
    }
    for (let key in values) {
      let value = values[key];
      if (Array.isArray(value)) {
        let list = parent.querySelector(`[data-form-list='${key}']`);
        if (list != null) {
          if (list.children.length > 0) {
            for (let i = list.children.length - 1; i > value.length; i--) {
              list.children.item(i)?.remove();
            }
            for (let i = list.children.length; i < value.length; i++) {
              let child = <Element>list.children.item(0);
              Haori.insertBefore(<Element>HaoriBind.clone(child), child);
            }
          }
        }
        let sample = value[0];
        if (typeof sample == 'object') {
          let groups = parent.querySelectorAll(`[data-form-object='${key}[]']`);
          groups.forEach((group, index) => {
            this.setValues(group, value[index]);
          });
        } else {
          let elements = this.getDirectChild(parent, key + '[]');
          if (elements.length > 0) {
            let type = elements[0].getAttribute('type');
            if (type == 'radio' || type == 'checkbox') {
              elements.forEach((element) => {
                for (let i = 0; i < value.length; i++) {
                  this.setValue(element, value[i]);
                }
              });
            } else {
              elements.forEach((element, index) => {
                if (value.length > index) {
                  this.setValue(element, value[index]);
                } else {
                  this.setValue(element, '');
                }
              });
            }
          } else {
            elements = this.getDirectChild(parent, key);
            elements.forEach((target) => {
              if (target.tagName == 'TEXTAREA' && !target.hasAttribute('data-form-join')) {
                return;
              }
              this.setValue(target, value.join('\n'));
            });
          }
        }
      } else if (typeof value == 'object') {
        let group = parent.querySelector(`[data-form-object='${key}']`);
        if (group != null) {
          this.setValues(group, value);
        }
      } else {
        let elements = this.getDirectChild(parent, key);
        elements.forEach((element) => {
          this.setValue(element, value);
        });
      }
    }
    if (parent.tagName == 'FORM' && bind && !parent.hasAttribute('data-form-fixed')) {
      let form = <HTMLFormElement>parent;
      let object = HaoriForm.getValues(form);
      if (form.hasAttribute('data-form-arg')) {
        let newObject: HashObject = {};
        newObject[form.getAttribute('data-form-arg')!] = object;
        object = newObject;
      }
      let json = JSON.stringify(object);
      if (json != form.getAttribute('data-bind')) {
        HaoriBind.setStopEventElements(form);
        form.setAttribute('data-bind', json);
        HaoriForm.click(form);
      }
    }
  }

  /**
   * 親エレメントのグループ（data-form-object属性を持っているもの）を含まない直接のエレメントのうちname属性が一致するもののリストを取得します。
   *
   * @param parent 親エレメント
   * @param name name属性の値
   */
  private static getDirectChild(parent: Element, name: string): Element[] {
    let results: Element[] = [];
    let groups = parent.querySelectorAll('[data-form-object]');
    let elements = parent.querySelectorAll(`[name='${name}']`);
    elements.forEach((element) => {
      let upGroup = element.closest('[data-form-object]');
      let ignore = false;
      groups.forEach((group) => {
        if (group == upGroup) {
          ignore = true;
        }
      });
      if (!ignore) {
        results.push(element);
      }
    });
    return results;
  }

  /**
   * エレメントに値を設定します。
   *
   * @param element エレメント
   * @param value 値
   */
  static setValue(element: Element, value: any) {
    value = value.toString();
    switch (element.tagName.toLowerCase()) {
      case 'input':
        switch (element.getAttribute('type')) {
          case 'checkbox':
          case 'radio':
            if (element.getAttribute('value') == value) {
              if (!(<HTMLInputElement>element).checked) {
                (<HTMLInputElement>element).checked = true;
              }
            } else if (
              (value == 'false' && element.getAttribute('value') == 'true') ||
              (value == 'true' && element.getAttribute('value') == 'false')
            ) {
              if ((<HTMLInputElement>element).checked) {
                (<HTMLInputElement>element).checked = false;
              }
            }
            break;
          case 'hidden':
            if (!element.hasAttribute('data-form-default')) {
              // hiddenの場合はvalue属性に値が設定されるためリセット用にもとの値を保持しておく
              element.setAttribute('data-form-default', element.getAttribute('value') || '');
            }
            if ((<HTMLInputElement>element).value != value) {
              (<HTMLInputElement>element).value = value;
            }
            break;
          default:
            if ((<HTMLInputElement>element).value != value) {
              (<HTMLInputElement>element).value = value;
            }
        }
        break;
      case 'select':
        if ((<HTMLSelectElement>element).value != value) {
          (<HTMLSelectElement>element).value = value;
        }
        break;
      case 'textarea':
        if ((<HTMLTextAreaElement>element).value != value) {
          (<HTMLTextAreaElement>element).value = value;
        }
        break;
      default:
        if (element.textContent != value) {
          element.textContent = value;
        }
    }
  }

  /**
   * フォームの決定処理を行います。同時にフォーム内のメッセージを削除します。
   *
   * @param form フォームエレメント
   */
  static submit(form: HTMLFormElement) {
    HaoriForm.clearAllMessages(form);
    form.submit();
  }

  /**
   * フォームのresetメソッドを呼び出します。
   * 同時にフォーム内のメッセージを削除し、data-form-list内の要素をひとつにします。
   *
   * @param form フォームエレメント
   * @param changeDataBind data-bind属性にも反映する場合はtrue
   */
  static reset(form: HTMLFormElement, changeDataBind = true) {
    HaoriForm.clearAllMessages(form);
    form.querySelectorAll('input[type="file"]').forEach((element) => {
      element.removeAttribute('data-form-data');
    });
    form.querySelectorAll('input[type="hidden"]').forEach((element) => {
      if (!element.hasAttribute('data-form-default')) {
        // hiddenの場合はvalue属性に値が設定されるためリセット用にもとの値を保持しておく
        element.setAttribute('data-form-default', element.getAttribute('value') || '');
      }
      (<HTMLInputElement>element).value = element.getAttribute('data-form-default')!;
    });
    form.querySelectorAll('[data-form-list]').forEach((element) => {
      if (element.children.length == 0) {
        return;
      }
      let template = <Element>element.children.item(0);
      element.innerHTML = '';
      element.appendChild(template);
    });
    form.querySelectorAll('[data-form-reset-bind]').forEach((element) => {
      element.removeAttribute('data-bind');
      element.removeAttribute('data-bind-fetch-current');
    });
    form.reset();
    if (changeDataBind && !form.hasAttribute('data-form-fixed')) {
      // data-bind-ifなどに反映するため一旦data-bindを空にしてから設定し直す
      form.setAttribute('data-bind', '{}');
      setTimeout(() => {
        let object = HaoriForm.getValues(form);
        if (form.hasAttribute('data-form-arg')) {
          let newObject: HashObject = {};
          newObject[form.getAttribute('data-form-arg')!] = object;
          object = newObject;
        }
        let json = JSON.stringify(object);
        HaoriBind.setStopEventElements(form);
        form.setAttribute('data-bind', json);
        HaoriForm.click(form);
      });
    }
  }

  /**
   * 親エレメントに成功メッセージを追加します。既に完了メッセージが存在する場合は先に削除します。
   *
   * @param parent 親エレメント
   * @param message メッセージ
   */
  static addSuccessMessage(parent: Element, message: string) {
    HaoriForm.addMessage(parent, message);
  }

  /**
   * 親エレメント内の入力エレメントにエラーメッセージを追加します。名称が一致しないメッセージは親エレメントに追加します。
   * data-form-object属性のエレメントがある場合は入れ子として扱います。
   *
   * @param parent 親エレメント
   * @param name 名称
   * @param message メッセージ
   */
  static addErrorMessage(parent: Element, name: string | null, message: string) {
    if (name == null) {
      name = '';
    }
    let elements = parent.querySelectorAll(`[name='${name}']`);
    if (elements.length == 0) {
      let dotPoint = name.indexOf('.');
      if (dotPoint > 0) {
        let prefix = name.substring(0, dotPoint);
        let indexStartPoint = prefix.indexOf('[');
        let indexLastPoint = prefix.lastIndexOf(']');
        let index = -1;
        if (indexStartPoint > 0 && indexLastPoint > 0 && indexStartPoint < indexLastPoint) {
          index = parseInt(prefix.substring(indexStartPoint + 1, indexLastPoint));
          prefix = prefix.substring(0, indexStartPoint) + '[]';
        }
        let groups = parent.querySelectorAll(`[data-form-object='${prefix}']`);
        if (groups.length > 0) {
          if (index < 0) {
            groups.forEach((group) => {
              HaoriForm.addErrorMessage(group, name == null ? null : name.substring(dotPoint + 1), message);
            });
            return;
          } else if (groups.length > index) {
            HaoriForm.addErrorMessage(groups[index], name == null ? null : name.substring(dotPoint + 1), message);
            return;
          }
        }
      }
      let groups = parent.querySelectorAll(`[data-form-object='${name}']`);
      if (groups.length == 0) {
        parent.classList.add(HaoriForm.options.invalidClassName);
        HaoriForm.addMessage(parent, message);
      } else {
        groups.forEach((group) => {
          group.classList.add(HaoriForm.options.invalidClassName);
          HaoriForm.addMessage(group, message);
        });
      }
      return;
    }
    elements.forEach((element: Element) => {
      let group = element.closest('[data-form-object]');
      if (parent.getAttribute('data-form-object') == null) {
        if (group != null) {
          // グループの下にあるためスキップ
          return;
        }
      } else {
        if (parent != group) {
          // グループが一致しない（複数の入れ子になっている）ためスキップ
          return;
        }
      }
      element.classList.add(HaoriForm.options.invalidClassName);
      if (element.parentElement != null) {
        element.parentElement.classList.add(HaoriForm.options.invalidClassName);
        HaoriForm.addMessage(element.parentElement, message);
      }
      let id = element.getAttribute('id');
      if (id != null) {
        let label = parent.querySelector(`[for='${id}']`);
        if (label != null) {
          label.classList.add(HaoriForm.options.invalidClassName);
        }
      }
    });
  }

  /**
   * 親エレメント内のすべてのメッセージを削除します。
   *
   * @param parent 親エレメント
   */
  static clearAllMessages(parent: Element) {
    parent.removeAttribute('data-message');
    parent.classList.remove(HaoriForm.options.invalidClassName);
    parent.querySelectorAll('[data-message]').forEach((element: Element) => {
      element.removeAttribute('data-message');
    });
    parent.querySelectorAll(`.${HaoriForm.options.invalidClassName}`).forEach((element: Element) => {
      element.classList.remove(HaoriForm.options.invalidClassName);
    });
    parent.dispatchEvent(
      new CustomEvent('clear-messages', {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  /**
   * エレメントにメッセージ属性を追加します。
   *
   * @param element 対象エレメント
   * @param message メッセージ
   */
  private static addMessage(element: Element, message: string) {
    let current = element.getAttribute('data-message');
    if (current == null || current == '') {
      current = message;
    } else {
      current += '\n' + message;
    }
    element.setAttribute('data-message', current);
    element.dispatchEvent(
      new CustomEvent('add-message', {
        bubbles: true,
        cancelable: true,
        composed: true,
        detail: message,
      })
    );
  }

  /**
   * リスト内の対象エレメントの後ろにエレメントを追加します。
   * 追加されるエレメントは対象エレメントをクローンしリセットしたものになります。
   *
   * @param element 対象エレメント
   */
  static listAdd(element: Element) {
    let child = this.getListChild(element);
    if (child == null) {
      console.error('data-form-list属性を持つエレメントが見つかりません', element);
      return;
    }
    let clone = <Element>HaoriBind.clone(child);
    let tmpForm = document.createElement('form');
    tmpForm.appendChild(clone);
    this.reset(tmpForm);
    Haori.insertAfter(clone, child);
    clone.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  /**
   * リスト内の対象エレメントを削除します。
   *
   * @param element  対象エレメント
   */
  static listDelete(element: Element) {
    let child = this.getListChild(element);
    if (child == null) {
      console.error('data-form-list属性を持つエレメントが見つかりません', element);
      return;
    }
    let parent = <Element>child.parentElement;
    if (parent.children.length == 1) {
      // 最後の子の場合は削除しない
      return;
    }
    child.remove();
    parent.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  /**
   * リスト内の対象エレメントを上に移動します。
   * 一番上の場合は何もしません。
   *
   * @param element 対象エレメント
   */
  static listPrev(element: Element) {
    let child = this.getListChild(element);
    if (child == null) {
      console.error('data-form-list属性を持つエレメントが見つかりません', element);
      return;
    }
    let prev = child.previousElementSibling;
    if (prev == null) {
      return;
    }
    Haori.insertBefore(child, prev);
    child.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  /**
   * リスト内の対象エレメントを下に移動します。
   * 一番下の場合は何もしません。
   *
   * @param element 対象エレメント
   */
  static listNext(element: Element) {
    let child = this.getListChild(element);
    if (child == null) {
      console.error('data-form-list属性を持つエレメントが見つかりません', element);
      return;
    }
    let next = child.nextElementSibling;
    if (next == null) {
      return;
    }
    Haori.insertAfter(child, next);
    child.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  /**
   * 先祖エレメントの中で親エレメントにdata-form-list属性を持つエレメントを取得します。
   * 存在しない場合はnullを返します。
   *
   * @param element 対象エレメント
   */
  private static getListChild(element: Element): Element | null {
    let parent = element.parentElement;
    if (parent == null) {
      return null;
    }
    if (parent.hasAttribute('data-form-list')) {
      return element;
    }
    return this.getListChild(parent);
  }

  /**
   * data-form-click属性の処理を行います。
   *
   * @param form フォームエレメント
   */
  public static click(form: HTMLFormElement) {
    Haori.hasAttribute(form, 'data-form-change-click', (target, value) => {
      let element = Haori.querySelector(value);
      if (element) {
        element.dispatchEvent(
          new CustomEvent('click', {
            bubbles: true,
            cancelable: true,
            composed: true,
          })
        );
      }
    });
  }
}

/**
 * メッセージを格納するクラスです。
 */
class HaoriFormMessage {
  /** 名称 */
  name: string;

  /** メッセージ */
  message: string;
}

/**
 * オプション設定を行うクラスです。
 */
class HaoriFormOptions {
  /** エラーメッセージ追加時に付与されるクラス名 */
  invalidClassName: string;

  constructor() {
    this.invalidClassName = 'invalid';
  }
}
HaoriForm.options = new HaoriFormOptions();

// data-bind属性変更処理
document.body.addEventListener('change-bind', (event) => {
  let form: HTMLFormElement | null = null;
  if ((<Element>event.target).tagName == 'FORM') {
    form = <HTMLFormElement>event.target;
  } else {
    form = (<Element>event.target).closest('form');
  }
  if (form == null) {
    return;
  }
  let object = JSON.parse(form.getAttribute('data-bind') || '{}');
  if (form.hasAttribute('data-form-arg')) {
    let newObject: HashObject = {};
    newObject = object[form.getAttribute('data-form-arg')!];
    object = newObject;
  }
  HaoriForm.setValues(form, object, false);
});

// fetch成功処理
document.body.addEventListener('fetch', (event: any) => {
  if ((<Element>event.target).tagName != 'FORM') {
    return;
  }
  if (event.detail.response.ok) {
    let form = <HTMLFormElement>event.target;
    if (!form.hasAttribute('data-bind-fetch')) {
      return;
    }
    let object = JSON.parse(form.getAttribute('data-bind') || '{}');
    HaoriForm.setValues(form, object, false);
  }
});

// リセットボタンクリック処理
document.body.addEventListener('click', (event) => {
  let element = <Element>event.target;
  if (element.tagName != 'BUTTON' || element.getAttribute('type') != 'reset') {
    element = <Element>element.closest('button,input');
    if (element == null || element.getAttribute('type') != 'reset') {
      return;
    }
  }
  let form = <HTMLFormElement>element.closest('form');
  if (form == null) {
    return;
  }
  // フォームのリセット処理に変更する
  event.preventDefault();
  HaoriForm.reset(form);
});

// フォーム内の入力欄変更処理
document.body.addEventListener('change', (event) => {
  let form = (<Element>event.target).closest('form');
  if (form == null) {
    return;
  }
  let object = HaoriForm.getValues(form);
  if (form.hasAttribute('data-form-arg')) {
    let newObject: HashObject = {};
    newObject = object[form.getAttribute('data-form-arg')!];
    object = newObject;
  }
  if (!form.hasAttribute('data-form-fixed')) {
    let json = JSON.stringify(object);
    if (json != form.getAttribute('data-bind')) {
      HaoriBind.setStopEventElements(form);
      form.setAttribute('data-bind', json);
      setTimeout(() => {
        HaoriForm.click(form);
      });
    }
  }
});

// ファイル選択処理
document.addEventListener('change', (event) => {
  let element = <HTMLElement>event.target;
  if (
    element.tagName != 'INPUT' ||
    element.getAttribute('type') != 'file' ||
    (!element.hasAttribute('data-form-file-data') && !element.hasAttribute('data-form-file-text'))
  ) {
    return;
  }
  Array.prototype.forEach.call((<HTMLInputElement>element).files, (file: File) => {
    let reader = new FileReader();
    reader.onloadend = () => {
      let data = reader.result as string;
      if (element.hasAttribute('data-form-file-data')) {
        data = data.substring(data.indexOf(',') + 1);
      }
      let dataSelector = element.getAttribute('data-form-file-data') || element.getAttribute('data-form-file-text');
      if (dataSelector == '') {
        element.setAttribute('data-form-data', data);
      } else {
        let elements = document.querySelectorAll(dataSelector!);
        if (elements.length == 0) {
          element
            .closest('form')
            ?.querySelectorAll('[name="' + dataSelector + '"]')
            .forEach((target) => {
              HaoriForm.setValue(target, data);
            });
        } else {
          elements.forEach((target) => {
            HaoriForm.setValue(target, data);
          });
        }
      }
      let form = element.closest('form');
      if (element.hasAttribute('data-form-file-type')) {
        let target = form?.querySelector('[name="' + element.getAttribute('data-form-file-type') + '"]');
        (<HTMLInputElement>target).value = file.type;
      }
      if (element.hasAttribute('data-form-file-name')) {
        let target = form?.querySelector('[name="' + element.getAttribute('data-form-file-name') + '"]');
        (<HTMLInputElement>target).value = file.name;
      }
      element.dispatchEvent(
        new CustomEvent('load', {
          bubbles: true,
          cancelable: true,
          composed: true,
          detail: file,
        })
      );
    };
    if (element.hasAttribute('data-form-file-data')) {
      reader.readAsDataURL(file);
    } else if (element.hasAttribute('data-form-file-text')) {
      reader.readAsText(file, element.getAttribute('data-form-file-encoding') || 'utf-8');
    }
  });
});

// ボタンクリック処理
document.addEventListener('click', (event) => {
  let target = <HTMLElement>event.target;
  Haori.hasClosestAttribute(target, 'data-form-list-add', (element) => {
    HaoriForm.listAdd(element);
  });
  Haori.hasClosestAttribute(target, 'data-form-list-delete', (element) => {
    HaoriForm.listDelete(element);
  });
  Haori.hasClosestAttribute(target, 'data-form-list-prev', (element) => {
    HaoriForm.listPrev(element);
  });
  Haori.hasClosestAttribute(target, 'data-form-list-next', (element) => {
    HaoriForm.listNext(element);
  });
});
